import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";
import { motion } from "framer-motion";
import * as React from "react";

import { cn } from "@/utils";

const Tabs = TabsPrimitive.Root;

const tabsTriggerVariants = cva(
  "cursor-pointer bg-transparent h-full border-0 flex items-center justify-center font-medium transition-all min-w-fit whitespace-nowrap rounded-t-md ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-primary/10 data-[state=active]:text-primary hover:data-[state=active]:bg-primary/15 data-[state=inactive]:text-muted-foreground hover:data-[state=inactive]:text-foreground hover:data-[state=inactive]:bg-muted/50",
  {
    variants: {
      size: {
        xs: "px-2 py-1.5 gap-1 text-xs",
        sm: "px-3 py-2 gap-1.5 text-xs",
        default: "px-5 py-3 gap-2 text-sm",
        lg: "px-6 py-4 gap-2.5 text-base",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

const TabsListContext = React.createContext<{
  registerTrigger: (element: HTMLElement | null, value: string) => void;
} | null>(null);

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const [underlineWidth, setUnderlineWidth] = React.useState(0);
  const [underlineLeft, setUnderlineLeft] = React.useState(0);
  const triggerRefs = React.useRef<Map<string, HTMLElement>>(new Map());
  const listRef = React.useRef<HTMLDivElement>(null);

  const registerTrigger = React.useCallback(
    (element: HTMLElement | null, value: string) => {
      if (element) {
        triggerRefs.current.set(value, element);
      } else {
        triggerRefs.current.delete(value);
      }
    },
    [],
  );

  const updateUnderline = React.useCallback(() => {
    // Find the active trigger by looking for the one with data-state="active"
    if (listRef.current) {
      const activeTrigger = listRef.current.querySelector(
        '[data-state="active"]',
      ) as HTMLElement;
      if (activeTrigger) {
        setUnderlineWidth(activeTrigger.offsetWidth);
        setUnderlineLeft(activeTrigger.offsetLeft);
      }
    }
  }, []);

  // Update underline when DOM changes (including active state changes)
  React.useEffect(() => {
    updateUnderline();

    // Use MutationObserver to watch for attribute changes
    if (listRef.current) {
      const observer = new MutationObserver(updateUnderline);
      observer.observe(listRef.current, {
        attributes: true,
        attributeFilter: ["data-state"],
        subtree: true,
      });

      return () => observer.disconnect();
    }
  }, [updateUnderline]);

  // Update underline on window resize
  React.useEffect(() => {
    const handleResize = () => updateUnderline();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [updateUnderline]);

  // Forward refs
  React.useImperativeHandle(ref, () => listRef.current!);

  return (
    <TabsListContext.Provider value={{ registerTrigger }}>
      <TabsPrimitive.List
        ref={listRef}
        className={cn("relative inline-flex items-center border-b", className)}
        {...props}
      >
        {props.children}
        <motion.div
          className="absolute bottom-0 h-0.5 bg-foreground z-10"
          initial={false}
          animate={{ width: underlineWidth, left: underlineLeft }}
          transition={{ duration: 0.2 }}
        />
      </TabsPrimitive.List>
    </TabsListContext.Provider>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

export interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, size, value, ...props }, ref) => {
  const context = React.useContext(TabsListContext);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (context && triggerRef.current && value) {
      context.registerTrigger(triggerRef.current, value);
    }
    return () => {
      if (context && value) {
        context.registerTrigger(null, value);
      }
    };
  }, [context, value]);

  // Forward both refs
  React.useImperativeHandle(ref, () => triggerRef.current!);

  return (
    <TabsPrimitive.Trigger
      ref={triggerRef}
      value={value}
      className={cn(tabsTriggerVariants({ size }), className)}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger, tabsTriggerVariants };
